// Firebase bootstrap. Reads config from Vite env vars (see .env.example).
//
// Design goal: the app must ALWAYS work, even with no Firebase config. If the
// config is missing or still a placeholder, `firebaseReady` resolves to null and
// the storage layer transparently falls back to localStorage only.

// Runtime imports are dynamic (see firebaseReady) so the Firebase SDK is split
// into its own chunk and the games stay interactive while it loads in the
// background. Only types are imported statically (erased at build time).
import type { FirebaseApp } from "firebase/app";
import type { Auth } from "firebase/auth";
import type { Firestore } from "firebase/firestore";

// Defensive env access: import.meta.env only exists under Vite; outside it
// (e.g. the Node-based level/merge test scripts import this module) fall back
// to an empty object so the module still loads and reports "not configured".
const env: Partial<ImportMetaEnv> = (import.meta as { env?: ImportMetaEnv }).env ?? {};

const config = {
  apiKey: env.VITE_FIREBASE_API_KEY,
  authDomain: env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: env.VITE_FIREBASE_APP_ID,
};

export function isFirebaseConfigured(): boolean {
  return Boolean(config.apiKey && config.projectId && config.appId);
}

export interface FirebaseHandles {
  app: FirebaseApp;
  auth: Auth;
  db: Firestore;
  uid: string;
}

let readyPromise: Promise<FirebaseHandles | null> | null = null;

/**
 * Initialise Firebase once and sign in anonymously. Resolves with handles
 * (including the resolved uid) or null when Firebase is not usable.
 */
export function firebaseReady(): Promise<FirebaseHandles | null> {
  if (readyPromise) return readyPromise;

  if (!isFirebaseConfigured()) {
    readyPromise = Promise.resolve(null);
    return readyPromise;
  }

  readyPromise = (async () => {
    try {
      const { initializeApp } = await import("firebase/app");
      const { getAuth, signInAnonymously, onAuthStateChanged } = await import("firebase/auth");
      const { initializeFirestore, persistentLocalCache, persistentMultipleTabManager } = await import(
        "firebase/firestore"
      );

      const app = initializeApp(config);
      // Offline persistence keeps progress writable without a connection and
      // syncs once back online. Multi-tab manager avoids errors with 2+ tabs.
      const db = initializeFirestore(app, {
        localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() }),
      });
      const auth = getAuth(app);

      const uid = await new Promise<string>((resolve, reject) => {
        let settled = false;
        let unsub: () => void = () => {};
        const finish = (fn: () => void) => {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          unsub();
          fn();
        };
        // If auth never resolves (e.g. network blocked), fall back to local-only
        // rather than hanging the cloud-sync init forever.
        const timer = setTimeout(() => finish(() => reject(new Error("anonymous auth timed out"))), 8000);
        unsub = onAuthStateChanged(
          auth,
          (user) => {
            if (user) finish(() => resolve(user.uid));
          },
          (err) => finish(() => reject(err)),
        );
        signInAnonymously(auth).catch((err) => finish(() => reject(err)));
      });

      return { app, auth, db, uid };
    } catch (err) {
      console.warn("[Logika] Firebase init failed, falling back to local storage:", err);
      return null;
    }
  })();

  return readyPromise;
}
